<template>
  <section class="latest-stories">
    <div class="flex items-center justify-between mb-8">
      <h2 class="text-2xl font-bold text-slate-900">Latest Stories by Category</h2>
    </div>

    <div class="space-y-12">
      <div v-for="(category, index) in categories" :key="index" class="category-section">
        <!-- Category Header -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h3 class="text-xl font-semibold text-slate-900 mb-2">{{ category.name }}</h3>
          </div>
          <div v-if="category.articles.length > maxArticlesPerCategory" class="mt-3 sm:mt-0">
            <router-link
              :to="'/theme/' + category.name.toLowerCase()"
              class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
            >
              View All
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </router-link>
          </div>
        </div>

        <!-- Articles Grid -->
        <div v-if="category.articles.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <ArticleCard
            v-for="(article, idx) in category.articles.slice(0, maxArticlesPerCategory)"
            :key="idx"
            :imageBase64="article.imageBase64"
            :title="article.title"
            :description="article.description"
            :slug="article.slug"
            :tags="article.tags"
            class="article-card"
          />
        </div>

        <!-- Ad Placement after first category -->
        <div v-if="index === 0" class="my-8">
          <div class="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-dashed border-purple-300 rounded-lg p-6 text-center">
            <div class="text-purple-600 text-sm font-medium mb-2">Advertisement</div>
            <div class="text-purple-500 text-xs">728x90 Leaderboard</div>
          </div>
        </div>

        <!-- Show More Button -->
        <div v-if="category.articles.length > maxArticlesPerCategory" class="mt-6 text-center">
          <router-link
            :to="'/theme/' + category.name.toLowerCase()"
            class="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium transition-colors"
          >
            Show {{ category.articles.length - maxArticlesPerCategory }} More Articles
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </router-link>
        </div>

        <!-- Ad Placement after second category -->
        <div v-if="index === 1" class="my-8 flex justify-center">
          <div
            class="bg-gradient-to-br from-orange-100 to-red-100 border-2 border-dashed border-orange-300 rounded-lg p-6 text-center w-[320px] h-[270px] flex flex-col justify-center"
          >
            <div class="text-orange-600 text-sm font-medium mb-2">Advertisement</div>
            <div class="text-orange-500 text-xs">300x250 Medium Rectangle</div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="category.articles.length === 0" class="text-center py-8 text-slate-500">
          <div class="w-12 h-12 mx-auto mb-3 bg-slate-100 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
          </div>
          <p class="text-sm">No articles in {{ category.name }} yet</p>
        </div>
      </div>
    </div>

    <!-- Summary Footer -->
    <div v-if="categories.length > 0" class="mt-12 pt-8 border-t border-slate-200 text-center">
      <p class="text-slate-600 text-sm">
        <span class="mx-2">•</span>
        <span class="text-blue-600">Browse by category above for more content</span>
        <span class="mx-2">•</span>
      </p>
    </div>
  </section>
</template>

<script setup>
  import { computed } from 'vue';
  import ArticleCard from '../Common/ArticleCard.vue';

  const props = defineProps({
    articles: {
      type: Array,
      required: true,
    },
    maxArticlesPerCategory: {
      type: Number,
      default: 6,
    },
  });

  // Group articles by their section property (default to "General" if missing)
  const categories = computed(() => {
    const groups = {};
    props.articles.forEach((article) => {
      const section = article.section ? article.section : 'General';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(article);
    });

    // Convert the groups object into an array of objects with name and articles
    // Sort articles within each category by date (newest first)
    return Object.entries(groups)
      .map(([name, articles]) => ({
        name,
        articles: articles.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort categories alphabetically
  });

  // Calculate total articles across all categories
  const totalArticles = computed(() => {
    return props.articles.length;
  });

  // Calculate how many articles are being displayed
  const displayedArticles = computed(() => {
    return categories.value.reduce((total, category) => {
      return total + Math.min(category.articles.length, props.maxArticlesPerCategory);
    }, 0);
  });
</script>

<style scoped>
  /* Category section styling */
  .category-section {
    scroll-margin-top: 100px;
  }

  /* Article card hover effects */
  .article-card {
    transform: translateY(0);
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  }

  .article-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }

  /* Grid responsiveness */
  .grid {
    gap: 1.5rem;
  }

  @media (max-width: 640px) {
    .grid {
      gap: 1rem;
    }
  }

  /* Button hover effects */
  .inline-flex:hover {
    transform: translateX(2px);
  }

  /* Smooth transitions */
  * {
    transition-property: transform, box-shadow, background-color, border-color, color;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
  }
</style>
